import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Player } from '@lordicon/react';
import {
  useTranslator,
  // FormikInput as Input,
  useToast,
  callNativeAppRemoveLastRoute,
  useAppContext,
} from '@front-activerse/common-components';
import {
  Container,
  Row,
  Col,
  Button,
  Alert,
  Spinner,
  Form,
  InputGroupText,
  Input,
  Label,
} from 'reactstrap';
import Layout from '../../Layout/Layout';
import TitleBar from '../../Layout/TitleBar';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Ingredient } from '../types';
import { withForcedAiError } from '../helpers/devForcedError';

const MULTIPLIER_STORAGE_KEY = 'tracker_multiplier';
const ADD_INGREDIENT_RETURN_STORAGE_KEY = 'tracker_add_ingredient_return_state';
const ADD_INGREDIENT_RETURN_EVENT = 'tracker_add_ingredient_return';
const INSTANT_SEARCH_DEBOUNCE_MS = 500;
const INSTANT_SEARCH_MIN_LENGTH = 3;

interface AnalyzeIngredientInput {
  description: string;
  baseAmount: number;
  baseUnit: string;
}

interface AnalyzeIngredientResponse {
  __typename: string;
  primaryIngredient: {
    productId: string;
    name: string;
    brand?: string;
    baseAmount: number;
    baseUnit: string;
    calories: number;
    proteinGrams: number;
    carbsGrams: number;
    fatGrams: number;
  };
  alternateIngredients: {
    productId: string;
    name: string;
    brand?: string;
    baseAmount: number;
    baseUnit: string;
    calories: number;
    proteinGrams: number;
    carbsGrams: number;
    fatGrams: number;
  }[];
}

interface TrackerAddIngredientContainerProps {
  analyzeIngredient: (input: AnalyzeIngredientInput) => Promise<AnalyzeIngredientResponse>;
  cancelAnalyzeIngredient: () => void;
}

interface AddIngredientLocationState {
  ingredients?: Ingredient[];
  mealName?: string;
  returnPath?: string;
  dietMealId?: string;
  servings?: number;
}

const TrackerAddIngredientContainer: React.FC<TrackerAddIngredientContainerProps> = ({
  analyzeIngredient,
  cancelAnalyzeIngredient,
}) => {
  const { t, language } = useTranslator();
  const playerRefSearch = useRef<Player>(null);
  const iconsIconMiddleColor = useMemo(() => {
    if (typeof document === 'undefined') return '#000000';
    const color = getComputedStyle(document.documentElement)
      .getPropertyValue('--icons-icon-middle-color')
      .trim();
    return color || '#000000';
  }, []);
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as AddIngredientLocationState | null;

  const ingredients = locationState?.ingredients ?? [];
  const mealName = locationState?.mealName ?? '';
  const returnPath = locationState?.returnPath;
  const servings = locationState?.servings;

  const { toastError } = useToast();
  const isCanceledRef = useRef(false);
  const searchRequestIdRef = useRef(0);

  const [showNoFoodAlert, setShowNoFoodAlert] = useState(false);
  // The AI daily limit can refuse this search too (analyzeIngredient shares the rate limiter with
  // the scan flows). Shown as an in-content Alert, like the no-food case, rather than a red toast.
  const [showRateLimitAlert, setShowRateLimitAlert] = useState(false);
  const [results, setResults] = useState<any>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [isAiSearching, setIsAiSearching] = useState(false);
  const [description, setDescription] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);

  const decimalSeparator = language === 'pl-PL' ? ',' : '.';

  const {
    images: { scanBarcodeIcon, plus, aiBadgeTiny, userProductIcon },
    lotties: { searchIcon },
  } = useAppContext();

  // Get multiplier from sessionStorage (default to 1 if not found)
  const multiplier = parseFloat(sessionStorage.getItem(MULTIPLIER_STORAGE_KEY) || '1');

  // Get system of measures from localStorage
  const systemOfMeasures = localStorage.getItem('systemOfMeasuresNutrition') || 'si';

  const handleAddIngredient = (selectedIngredient: any) => {
    // Find a "portion" measure whose weight unit matches the product's base
    // dimension. Its weight becomes the default mass; nutrition is scaled from
    // the response (which is per response.baseAmount) to that weight.
    const portionMeasure = (selectedIngredient.measures ?? []).find(
      (m: any) =>
        m.unit === 'portion' &&
        m.amount?.value > 0 &&
        m.amount?.unit === selectedIngredient.baseUnit
    );

    const scale =
      portionMeasure && selectedIngredient.baseAmount > 0
        ? portionMeasure.amount.value / selectedIngredient.baseAmount
        : 1;

    // Stored per 1 meal-portion (divide by multiplier), scaled to the measure.
    const baseAmountFor1Portion =
      (portionMeasure ? portionMeasure.amount.value : selectedIngredient.baseAmount) / multiplier;
    const caloriesFor1Portion = (selectedIngredient.calories * scale) / multiplier;
    const proteinFor1Portion = (selectedIngredient.proteinGrams * scale) / multiplier;
    const fatFor1Portion = (selectedIngredient.fatGrams * scale) / multiplier;
    const carbsFor1Portion = (selectedIngredient.carbsGrams * scale) / multiplier;

    const baseMeasurement = {
      amount: baseAmountFor1Portion,
      text: `${baseAmountFor1Portion} ${selectedIngredient.baseUnit}`,
      unit: selectedIngredient.baseUnit,
    };

    // measurements[0] = kitchen measure (drives "≈ 1 porcja"), [1] = base mass.
    const measurements = portionMeasure
      ? [
          {
            measureId: portionMeasure.measureId,
            amount: 1,
            text: '1 portion',
            unit: 'portion',
          },
          baseMeasurement,
        ]
      : [baseMeasurement];

    const newIngredient: Ingredient = {
      id: selectedIngredient.productId,
      name: selectedIngredient.name,
      brand: selectedIngredient.brand,
      kcal: caloriesFor1Portion,
      // Carried into the meal so saving it can tell the backend, which has no way to work this out
      // on its own — a meal stores copies of the values, not a reference to the product.
      isUserProduct: Boolean(selectedIngredient.isUserProduct),
      measurements,
      macro: {
        protein: { grams: proteinFor1Portion },
        fat: { grams: fatFor1Portion },
        carbohydrates: { grams: carbsFor1Portion },
      },
    };
    if (returnPath) {
      const payload = {
        returnPath,
        ingredients: [...ingredients, newIngredient],
        mealName,
        ...(servings != null && { servings }),
        updatedAt: Date.now(),
      };
      sessionStorage.setItem(ADD_INGREDIENT_RETURN_STORAGE_KEY, JSON.stringify(payload));
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent(ADD_INGREDIENT_RETURN_EVENT, { detail: payload }));
      }
      navigate(-1);
    } else {
      navigate(-1);
    }
    callNativeAppRemoveLastRoute();
    // toastSuccess(<p>{t('tracker.meal.ingredients.addedConfirmation')}</p>);
  };

  const runSearch = async (
    chosenLookupStrategy: string,
    event?: React.FormEvent<HTMLFormElement>
  ) => {
    event?.preventDefault();
    if (description.trim() === '') {
      return;
    }
    setShowNoFoodAlert(false);
    setShowRateLimitAlert(false);
    setIsSearching(true);
    // Nie czyścimy wyników – stare pozostają widoczne do momentu otrzymania odpowiedzi

    if (isCanceledRef.current) {
      isCanceledRef.current = false;
      setIsSearching(false);
      setIsAiSearching(false);
      return;
    }

    const currentRequestId = ++searchRequestIdRef.current;
    const request = {
      description,
      baseAmount: systemOfMeasures === 'si' || systemOfMeasures === 'si_cl' ? 100 : 3,
      baseUnit: systemOfMeasures === 'si' || systemOfMeasures === 'si_cl' ? 'grams' : 'oz',
      lookupStrategy: chosenLookupStrategy,
    };

    try {
      const response = withForcedAiError(await analyzeIngredient(request));

      // Aktualizuj wyniki tylko jeśli to nadal aktualne zapytanie (nie przyszła nowsza odpowiedź)
      if (currentRequestId !== searchRequestIdRef.current) {
        return;
      }

      if (response.__typename === 'AnalyzedDailyTrackerIngredients') {
        const primaryIngredient = response.primaryIngredient;
        const alternateIngredients = response.alternateIngredients || [];

        setResults([primaryIngredient, ...alternateIngredients]);

        if (chosenLookupStrategy === 'llm_only') {
          handleAddIngredient(primaryIngredient);
          return;
        }
      } else if (response.__typename === 'NotFoodError') {
        setResults([]);
        setShowNoFoodAlert(true);
      } else if (response.__typename === 'ProductNotFoundError') {
        setResults([]);
      } else if (response.__typename === 'RateLimitExceeded') {
        // Without this the union member fell through to the generic red toast, which said nothing
        // about the limit being the reason.
        setResults([]);
        setShowRateLimitAlert(true);
      } else {
        toastError();
      }
    } catch (error) {
      if (isCanceledRef.current || (error as Error)?.name === 'AbortError') {
        return;
      }
      if (currentRequestId === searchRequestIdRef.current) {
        toastError();
      }
    } finally {
      if (currentRequestId === searchRequestIdRef.current) {
        setIsSearching(false);
        setIsAiSearching(false);
      }
    }
  };

  const handleSubmit = (lookupStrategy: string, event?: React.FormEvent<HTMLFormElement>) => {
    runSearch(lookupStrategy || 'db_only', event);
    searchInputRef.current?.blur();
  };

  // Instant search
  useEffect(() => {
    const trimmed = description.trim();
    if (trimmed.length < INSTANT_SEARCH_MIN_LENGTH) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      runSearch('db_only');
    }, INSTANT_SEARCH_DEBOUNCE_MS);

    return () => window.clearTimeout(timeoutId);
  }, [description]); // eslint-disable-line react-hooks/exhaustive-deps -- runSearch w timeout ma aktualny stan

  const handleSearchWithAI = async () => {
    if (description.trim() === '') {
      return;
    }
    setIsAiSearching(true);
    handleSubmit('llm_only');
  };

  const handleCancel = () => {
    isCanceledRef.current = true;
    cancelAnalyzeIngredient();
    setIsSearching(false);
    setIsAiSearching(false);
    setTimeout(() => {
      isCanceledRef.current = false;
    }, 0);
  };

  return (
    <Layout>
      <TitleBar showBackBtn={true}>{t('tracker.logMeal.addIngredient.navbarTitle')}</TitleBar>

      <div className="page-template-tracker edit-meal-page">
        <Container className="py-md-4 full-height">
          <Row>
            <Col xl={{ size: 10, offset: 1 }}>
              <div className="page-container">
                <div className="tracker-container">
                  <div className="d-flex justify-content-between gap-m">
                    <Form onSubmit={(e) => handleSubmit('db_only', e)} className="flex-grow-1">
                      <Label for="description">
                        {t('tracker.addIngredient.search.inputLabel')}
                      </Label>
                      <div
                        className="input-group-with-addon start"
                        onMouseEnter={() => playerRefSearch.current?.playFromBeginning()}
                        onTouchStart={() => playerRefSearch.current?.playFromBeginning()}
                      >
                        <InputGroupText>
                          {/* <img src={searchIcon} alt="search" /> */}
                          <Player
                            ref={playerRefSearch}
                            icon={searchIcon}
                            size={24}
                            colorize={iconsIconMiddleColor}
                          />
                        </InputGroupText>
                        <Input
                          id="search"
                          name="description"
                          type="search"
                          enterkeyhint="search"
                          value={description}
                          onChange={(e) => {
                            const val = e.target.value;
                            setDescription(val);
                            if (val.trim() === '') {
                              setResults(null);
                            }
                          }}
                          innerRef={searchInputRef}
                        />
                      </div>
                    </Form>

                    <Button
                      color="link"
                      tag={Link}
                      to="scan-barcode"
                      state={{ ingredients, mealName, returnPath, servings }}
                      className="p-0"
                      style={{ marginTop: '26px' }}
                    >
                      <img src={scanBarcodeIcon} width={40} height={40} alt="" />
                    </Button>
                  </div>
                </div>
                <hr className="my-0" />

                <div className="tracker-container">
                  {isAiSearching && (
                    <div className="voice-button is-submitting">
                      <div className="loader-container text-center">
                        <p className="mb-0 medium">{t('tracker.describeFood.loader.analyzing')}</p>
                      </div>
                    </div>
                  )}

                  {/* A <div>, not a <p>: Spinner renders a <div>, which is invalid inside a <p>
                      and made React log a nesting/hydration error on every search. */}
                  {description.trim() !== '' &&
                    isSearching &&
                    !isAiSearching &&
                    (!results || results.length === 0) && (
                      <div className="text-text-variant-color text-center">
                        <Spinner size="sm" className="me-2" />
                        {t('tracker.addIngredient.loader.searching')}
                      </div>
                    )}

                  {/* render results here - hide when search input is empty */}
                  {description.trim() !== '' && results && results.length > 0 && (
                    <>
                      <div className="d-flex justify-content-between align-items-center mb-m">
                        <p className="small medium semi-bold mb-0">
                          {t('tracker.addIngredient.search.resultsTitle')} ({results.length}):{' '}
                        </p>
                        {isSearching && !isAiSearching && (
                          // A <span>, not a <p>, for the same reason: Spinner is a <div>. d-flex
                          // keeps the spinner and the label on one line, as the <p> did.
                          <span className="text-text-variant-color small d-flex align-items-center">
                            <Spinner size="sm" className="me-2" />
                            {t('tracker.addIngredient.loader.searching')}
                          </span>
                        )}
                      </div>
                      <ul className="tracker-ingredients-search-results-list-items">
                        {results.map((ingredient: any, index: number) => (
                          <li
                            key={ingredient.id || `ingredient_${index}`}
                            className="tracker-ingredient-item"
                          >
                            <div className="d-flex justify-content-between">
                              <div>
                                {ingredient.brand && (
                                  <span className="description text-text-variant-color">
                                    {ingredient.brand}
                                  </span>
                                )}
                                <span className="small text-text-main-color semi-bold text-truncate-2-line">
                                  {/* Before the name, as in Figma 9892:15062 — the badge marks the
                                      row, so it reads first rather than trailing the label. */}
                                  {ingredient.isUserProduct && (
                                    <img
                                      src={userProductIcon}
                                      // The asset is 13x13; drawing it at 14 blurred the rounded
                                      // square's 1px stroke.
                                      width={13}
                                      height={13}
                                      // Centred on the label's cap-height, not its baseline: the
                                      // badge is 13px against a 10px cap, so every keyword value
                                      // left it visibly low (middle +0.8px, text-bottom +2.5px) or
                                      // high (baseline -1.5px). -2px from the baseline lands it
                                      // within 0.01px of the capitals' centre.
                                      style={{ verticalAlign: '-2px' }}
                                      className="me-1"
                                      alt={t('tracker.addIngredient.userProduct.label')}
                                      title={t('tracker.addIngredient.userProduct.label')}
                                    />
                                  )}
                                  {ingredient.name}
                                </span>

                                <span className="d-block description text-text-variant-color mt-xs">
                                  {ingredient.calories} kcal
                                  <span className="d-inline-block ms-3">
                                    {t('summary.macros.proteinsShort')}{' '}
                                    {(Math.round(ingredient.proteinGrams * 10) / 10)
                                      .toString()
                                      .replace('.', decimalSeparator)}
                                    g
                                  </span>
                                  <span className="d-inline-block ms-s">
                                    {t('summary.macros.fatsShort')}{' '}
                                    {(Math.round(ingredient.fatGrams * 10) / 10)
                                      .toString()
                                      .replace('.', decimalSeparator)}
                                    g
                                  </span>
                                  <span className="d-inline-block ms-s">
                                    {t('summary.macros.carbsShort')}{' '}
                                    {(Math.round(ingredient.carbsGrams * 10) / 10)
                                      .toString()
                                      .replace('.', decimalSeparator)}
                                    g
                                  </span>
                                </span>
                              </div>
                              <Button
                                color="link"
                                className="p-0"
                                onClick={() => handleAddIngredient(ingredient)}
                              >
                                <img src={plus} width={24} height={24} alt="" />
                              </Button>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </>
                  )}

                  {description.trim() === '' && (
                    <p className="text-text-variant-color text-center">
                      {t('tracker.addIngredient.placeholder.searching')}
                    </p>
                  )}

                  {description.trim() !== '' && results && (
                    <div className="search-no-results">
                      {/* Both states carry a heading, with different copy (Figma 4636:33025 for the
                          empty search, 4636:32980 when the list found something but maybe not the
                          right thing). The description only belongs to the empty state — in the
                          with-results variant it is hidden in the design. */}
                      <p
                        className={`search-no-results-title ${
                          results.length === 0 ? 'mb-xxs' : 'mb-l'
                        }`}
                      >
                        {results.length === 0
                          ? t('tracker.addIngredient.search.noResults.title')
                          : t('tracker.addIngredient.search.missingResults.title')}
                      </p>
                      {results.length === 0 && (
                        <p className="search-no-results-description mb-l">
                          {t('tracker.addIngredient.search.noResults.description')}
                        </p>
                      )}

                      <Button
                        type="button"
                        className="search-no-results-option"
                        onClick={() =>
                          navigate('add-product', {
                            state: {
                              ingredients,
                              mealName,
                              returnPath,
                              servings,
                              stepsBack: 2,
                              // "Add manually" means exactly that: a blank form. Without this the
                              // form picks up whatever label scan is still in sessionStorage (kept
                              // there for the "Analyze again" round trip) and arrives pre-filled
                              // with an unrelated product.
                              manualEntry: true,
                            },
                          })
                        }
                        // Disabled while AI is processing so the user can't leave mid-request.
                        disabled={isAiSearching}
                        data-testid="add-product-entry"
                        data-test-id="add_product_entry"
                      >
                        {t('tracker.addIngredient.search.manuallyOption')}
                      </Button>

                      <Button
                        type="button"
                        className="search-no-results-option"
                        onClick={handleSearchWithAI}
                        disabled={isAiSearching}
                      >
                        <img src={aiBadgeTiny} alt="" className="me-s" />
                        {t('tracker.addIngredient.search.aiOption')}
                      </Button>
                    </div>
                  )}

                  {showNoFoodAlert && (
                    <Alert color="warning" className="mt-xl mb-0" fade={false}>
                      <p className="mb-0">
                        {t('tracker.describeFood.analyze.noFood.notification')}
                      </p>
                    </Alert>
                  )}

                  {showRateLimitAlert && (
                    <Alert
                      // danger, not warning: the request was refused outright, unlike "nothing to
                      // analyze" above. Same colour the scan screens use for this case.
                      color="danger"
                      className="mt-xl mb-0"
                      fade={false}
                      data-testid="add-ingredient-rate-limit"
                      data-test-id="add_ingredient_rate_limit"
                    >
                      <p className="mb-0">{t('tracker.limitUsageError.notification')}</p>
                    </Alert>
                  )}
                </div>
              </div>
            </Col>
          </Row>
        </Container>

        {isAiSearching && (
          <div className="sticky-footer">
            <Container className="px-3 text-center">
              <Button type="button" color="link" onClick={handleCancel}>
                {t('tracker.describeFood.loader.cancelButton')}
              </Button>
            </Container>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default TrackerAddIngredientContainer;
